import { ArrowRight, ChevronDown } from 'lucide-react';
import { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { WHATSAPP_URL } from '@/constants/whatsapp';
// LCP image served from public/ for early discovery via <link rel="preload"> in index.html
const heroBanner = '/economia-tributaria-inteligente-hero.webp';

interface HeroProps {
  headline?: string;
  heroImage?: string;
}

export const Hero = ({ headline, heroImage: customHeroImage }: HeroProps) => {
  const sectionRef = useRef<HTMLElement>(null);
  const bgRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const displayHeadline = headline || t('hero.headline');
  const heroImage = customHeroImage || heroBanner;

  // Defer parallax scroll listener until after first paint
  useEffect(() => {
    const section = sectionRef.current;
    const bg = bgRef.current;
    const content = contentRef.current;
    const cta = ctaRef.current;
    if (!section || !bg) return;

    let ticking = false;
    let rafId: number;

    const update = () => {
      const top = section.getBoundingClientRect().top;
      const sectionH = section.offsetHeight;
      const progress = Math.max(0, Math.min(1, -top / sectionH));

      bg.style.transform = `translate3d(0,${progress * 30}%,0) scale(${1 + progress * 0.1})`;

      const opacity = String(Math.max(0, 1 - progress * 2));
      if (content) content.style.opacity = opacity;
      if (cta) cta.style.opacity = opacity;
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) { rafId = requestAnimationFrame(update); ticking = true; }
    };

    // Delay attaching scroll listener to avoid blocking first paint
    const timerId = requestAnimationFrame(() => {
      window.addEventListener('scroll', onScroll, { passive: true });
    });

    return () => {
      cancelAnimationFrame(timerId);
      cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  return (
    <section ref={sectionRef} className="relative w-full h-[70vh] md:h-screen overflow-hidden flex items-end justify-start pb-20 md:pb-32">
      {/* LCP element: real <img> with fetchpriority for fastest load */}
      <div ref={bgRef} className="absolute inset-0 w-full h-full z-0 will-change-transform">
        <img
          src={heroImage}
          alt="Economia tributária inteligente - Núcleo Soluções Contábeis"
          fetchPriority="high"
          decoding="sync"
          className="w-full h-full object-cover object-center"
        />
      </div>

      <div className="absolute inset-0 bg-gradient-to-t from-[rgba(0,20,40,0.85)] via-[rgba(0,20,40,0.4)] to-transparent z-[1]" />

      <div ref={contentRef} className="absolute inset-0 z-[2] flex flex-col justify-center items-start w-full max-w-[1400px] mx-auto px-[8%] text-white">
        <h1
          className="text-[clamp(1.75rem,5vw,3.25rem)] font-bold max-w-[90%] md:max-w-[520px] drop-shadow-sm text-secondary leading-[1.15] tracking-tight animate-hero-fadeup"
          style={{ animationDelay: '0.2s' }}
        >
          {displayHeadline}
        </h1>

        <div
          className="w-20 h-0.5 bg-gold mt-4 rounded-sm"
          style={{
            animation: 'hero-scalex 0.4s cubic-bezier(0.25,0.1,0.25,1) 0.4s both',
            transformOrigin: 'left',
          }}
        />
      </div>

      <div ref={ctaRef} className="absolute bottom-24 md:bottom-32 left-0 right-0 z-[2] w-full max-w-[1400px] mx-auto px-[8%]">
        <div
          className="flex flex-col md:flex-row justify-between items-center md:items-end w-full gap-8 animate-hero-fadeup"
          style={{ animationDelay: '0.9s' }}
        >
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-3 bg-white/20 backdrop-blur-md border border-white/35 shadow-lg text-secondary font-semibold text-base md:text-lg tracking-wide uppercase py-3.5 pl-7 md:pl-9 pr-3.5 rounded-full transition-all hover:bg-[#CDE3F3] hover:text-primary hover:border-[#CDE3F3] hover:-translate-y-0.5 hover:scale-[1.02] active:scale-[0.98] min-h-[48px]"
          >
            {t('hero.cta')}
            <span className="w-9 h-9 rounded-full bg-white/20 border border-white/35 backdrop-blur-md flex items-center justify-center transition-all group-hover:bg-primary group-hover:border-primary">
              <ArrowRight className="w-4 h-4 text-secondary transition-transform group-hover:translate-x-0.5" />
            </span>
          </a>

          <a
            href="#"
            onClick={(e: React.MouseEvent) => { e.preventDefault(); sectionRef.current?.nextElementSibling?.scrollIntoView({ behavior: 'smooth' }); }}
            className="hidden md:flex items-center gap-2 text-secondary/80 text-sm font-medium uppercase tracking-widest mb-3 hover:text-secondary transition-colors cursor-pointer animate-hero-fadeup"
            style={{ animationDelay: '1.2s' }}
          >
            {t('hero.explore')}
            <ChevronDown className="w-4 h-4 animate-bounce-subtle" />
          </a>
        </div>
      </div>
    </section>
  );
};
